<template lang="html">
  <div :style="`height:${windowHeight}px;width:100%`">
    <v-btn
      v-if="params('draft') === 'true'"
      title="Copier la position actuelle"
      icon
      style="position:absolute;top:8px;left:44px;z-index:1"
      @click="bboxPasted()"
    >
      <v-icon>mdi-crop-free</v-icon>
    </v-btn>
    <v-btn
      v-if="config.satelliteTiles"
      :title="satellite ? 'Vue plan' : 'Vue satellite'"
      fab
      absolute
      small
      style="top:150px;left:4px"
      @click="toggleSatellite()"
    >
      <v-icon v-if="satellite">
        mdi-layers
      </v-icon>
      <v-icon v-else>
        mdi-satellite
      </v-icon>
    </v-btn>
    <v-progress-linear
      v-if="loading"
      style="position:absolute;z-index:1;max-width:100%;pointer-events: none;"
      indeterminate
      color="primary"
    />
    <div
      id="map"
      style="height:100%"
    />
  </div>
</template>

<script>
import { bbox2zoom } from '../assets/map-utils'
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'

import 'maplibre-gl/dist/maplibre-gl.css'
import eventBus from '../assets/event-bus.js'

const fitBoundsOptions = { maxZoom: 16, duration: 0 }
const style = require('../assets/style')

const maplibregl = require('maplibre-gl')

const zoomLevel = {
  housenumber: 17,
  street: 16,
  municipality: 12
}

export default {
  data: () => ({
    parcelsRefreshed: false,
    metricAvg: null,
    dataTilesUrl: null,
    zoom: null,
    satellite: false
  }),
  computed: {
    ...mapState(['application', 'loading', 'parcel']),
    ...mapGetters(['datasetUrl', 'tileserverUrl', 'parcelleIdField', 'config', 'defaultColor', 'dateField', 'legend', 'linkField', 'attachmentField'])
  },
  async mounted () {
    this.initMap()
    eventBus.$on('goTo', (pos, zoom) => {
      this.$store.commit('setAny', { loading: true })
      this.map.setZoom(zoomLevel[pos[2]] || 11)
      this.map.setCenter([pos[1], pos[0]])
      setTimeout(() => {
        this.map.fire('click', { lngLat: { lat: pos[0], lng: pos[1] } })
      }, 1000)
    })
    eventBus.$on('query', (query) => {
      const select = [this.parcelleIdField.key]
      if (this.config.colorField) select.push(this.config.colorField)
      const dataTilesUrl = `${this.datasetUrl}/lines?format=pbf&size=10000&sort=_rand&select=${select.join(',') + query}&finalizedAt=${this.config.datasets[0].finalizedAt}&xyz={x},{y},{z}`
      if (this.map.isStyleLoaded()) {
        if (this.map.getSource('data-fair')) {
          this.map.removeLayer('parcelle-dataset-infos')
          this.map.removeLayer('parcelle-dataset-points')
          this.map.removeSource('data-fair')
        }
        this.parcelsRefreshed = false
        this.map.addSource('data-fair', { type: 'vector', tiles: [dataTilesUrl] })
        this.updateDataLayer()
      } else this.dataTilesUrl = dataTilesUrl
    })
    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.map.resize()
      }, 100)
    }, false)
  },
  methods: {
    initMap () {
      if (!maplibregl) return
      const bbox = (this.config.initialBounds && this.config.initialBounds.length) ? JSON.parse(this.config.initialBounds) : this.config.datasets[0].bbox
      const initialBounds = maplibregl.LngLatBounds.convert(bbox)
      const params = new URLSearchParams(window.location.search)
      this.zoom = params.get('zoom') ? Number(params.get('zoom')) : (initialBounds ? bbox2zoom(bbox) : 7)
      const center = params.get('lng') && params.get('lat') ? [Number(params.get('lng')), Number(params.get('lat'))] : (initialBounds ? initialBounds.getCenter().toArray() : [2.0833, 47.2167])
      this.map = new maplibregl.Map({
        container: 'map',
        center,
        zoom: this.zoom,
        style,
        transformRequest: (url, resourceType) => {
          const ret = { url }
          if (url.includes('/data-fair')) ret.credentials = 'include' // include cookies, for data-fair sessions
          return ret
        }
      })
      this.map.addControl(new maplibregl.NavigationControl(), 'top-left')
      this.map.addControl(new maplibregl.ScaleControl(), 'bottom-left')

      // Disable map rotation using right click + drag
      this.map.dragRotate.disable()
      // Disable map rotation using touch rotation gesture
      this.map.touchZoomRotate.disableRotation()

      this.map.on('click', (e) => this.showTooltip([e.lngLat.lat, e.lngLat.lng]))

      if (!params.get('zoom') && !params.get('lng') && !params.get('lat') && (!this.config.initialBounds || !this.config.initialBounds.length)) this.map.fitBounds(this.config.datasets[0].bbox, fitBoundsOptions)

      // Finalize map after first load
      this.map.once('load', () => {
        this.map.setPaintProperty('batiments', 'fill-opacity', 0.4)
        this.map.addSource('data-fair', { type: 'vector', tiles: [this.dataTilesUrl] })
        this.updateDataLayer()

        const qParams = new URLSearchParams(window.location.search)
        const parcelCode = qParams.get('selected') && qParams.get('selected').split(',').pop()
        if (parcelCode) {
          const parcel = this.map.querySourceFeatures('cadastre', { sourceLayer: 'parcelles', filter: ['in', 'id', parcelCode] }).pop()
          if (parcel) this.$store.commit('setAny', { parcel: parcel.properties })
        }
        // Layer for clicked/selected parcelle
        this.map.addLayer({
          id: 'parcelles-selected',
          type: 'line',
          source: 'cadastre',
          'source-layer': 'parcelles',
          layout: {},
          minzoom: 13,
          paint: {
            'line-color': '#FF3D00',
            'line-width': {
              stops: [[12, 0.75], [22, 6]]
            }
          },
          filter: ['in', 'id', parcelCode || '']
        }, 'poi_label')

        if (this.config.satelliteTiles) {
          this.map.addSource('satellite', {
            type: 'raster',
            tiles: [
              'https://data.geopf.fr/tms/1.0.0/ORTHOIMAGERY.ORTHOPHOTOS/{z}/{x}/{y}.jpeg'
            ]
          })
          this.map.addLayer({
            id: 'satellite-tiles',
            type: 'raster',
            source: 'satellite'
          }, 'parcelles')
          this.map.setLayoutProperty('satellite-tiles', 'visibility', 'none')
        }

        this.map.on('moveend', this.moved)
        if (this.config.geolocate) {
          this.geolocateControl.trigger()
        }
      })

      this.map.once('styledata', () => {
        this.initGeolocation()
      })

      // "idle" event seems to be the best indication that the map is fully loaded
      this.map.on('idle', () => {
        if (this.map.getZoom() >= 13 && !this.parcelsRefreshed) {
          const colors = Object.assign({}, ...this.legend.map(l => ({ [l[0]]: l[1] })))
          this.map.querySourceFeatures('data-fair', { sourceLayer: 'results' }).forEach(dfP => {
            if (this.parcelleIdField.separator) {
              // console.log(dfP.properties[this.parcelleIdField.key] || [])
              JSON.parse(dfP.properties[this.parcelleIdField.key] || '[]').forEach(id => {
                this.map.setFeatureState({
                  source: 'cadastre',
                  sourceLayer: 'parcelles',
                  id
                }, {
                  color: !this.config.colorField ? colors['*'] : (colors[dfP.properties[this.config.colorField]] || colors['*']),
                  opacity: 0.7
                })
              })
            } else {
              this.map.setFeatureState({
                source: 'cadastre',
                sourceLayer: 'parcelles',
                id: dfP.properties[this.parcelleIdField.key]
              }, {
                color: !this.config.colorField ? colors['*'] : (colors[dfP.properties[this.config.colorField]] || colors['*']),
                opacity: 0.7
              })
            }
          })
          this.parcelsRefreshed = true
          this.$store.commit('setAny', { loading: false })
          if (window.triggerCapture && this.map.getSource('data-fair')) setTimeout(() => window.triggerCapture(), 5000)
        } else {
          this.$store.commit('setAny', { loading: false })
          if (window.triggerCapture && this.map.getSource('data-fair')) window.triggerCapture()
        }
      })

      if (this.config.tooltip.length) {
        this.map.on('mousemove', (e) => {
          if (this.map.isStyleLoaded() && this.map.getZoom() >= 13) {
            const parcels = this.map.queryRenderedFeatures(this.map.project([e.lngLat.lng, e.lngLat.lat]), {
              layers: ['parcelles']
            }).filter(p => p.state && Object.keys(p.state).length)
            this.map.getCanvas().style.cursor = parcels.length ? 'pointer' : ''
          }
        })
      }
    },
    moved (e) {
      const params = new URLSearchParams(window.location.search)
      if (e.target.getZoom() < 13 && this.zoom >= 13) {
        this.map.setFilter('parcelles-selected', ['in', 'id', ''])
        this.$store.commit('setAny', { parcel: null })
      } else if (e.target.getZoom() >= 13 && this.zoom < 13) {
        const parcelCode = params.get('selected') && params.get('selected').split(',').pop()
        if (parcelCode) {
          const parcel = this.map.querySourceFeatures('cadastre', { sourceLayer: 'parcelles', filter: ['in', 'id', parcelCode] }).pop()
          if (parcel) {
            this.$store.commit('setAny', { parcel: parcel.properties })
            this.map.setFilter('parcelles-selected', ['in', 'id', parcelCode])
          }
        }
      }
      this.zoom = e.target.getZoom()
      this.$store.commit('setAny', { loading: true })
      this.parcelsRefreshed = false
      params.set('zoom', e.target.getZoom())
      const { lat, lng } = e.target.getCenter()
      params.set('lng', lng)
      params.set('lat', lat)
      window.history.replaceState(null, null, window.location.pathname + '?' + params.toString())
    },
    async showTooltip (pos) {
      this.tooltipPos = pos
      const lngLat = [this.tooltipPos[1], this.tooltipPos[0]]

      if (this.config.tooltip.length) {
        const features = this.map.queryRenderedFeatures(this.map.project(lngLat), {
          layers: this.map.getZoom() >= 13 ? ['parcelles'] : ['parcelle-dataset-points']
        })
        const qParams = new URLSearchParams(window.location.search)
        if (!features.length) {
          this.$store.commit('setAny', { features: null })
          qParams.delete('selected')
          window.history.replaceState(null, null, window.location.pathname + '?' + qParams.toString())
          return
        } else {
          if (this.map.getZoom() >= 13) {
            const parcel = features[0]
            if (parcel && (!this.parcel || this.parcel.id !== parcel.properties.id)) {
              this.map.setFilter('parcelles-selected', ['in', 'id', parcel.properties.id])
              this.$store.commit('setAny', { parcel: parcel.properties })
            } else {
              this.map.setFilter('parcelles-selected', ['in', 'id', ''])
              this.$store.commit('setAny', { parcel: undefined, features: [] })
            }
          } else {
            this.$store.commit('setAny', { parcel: null })
          }
        }
        const ids = this.map.getZoom() >= 13 ? [features[0].properties.id] : [].concat(...features.map(f => this.parcelleIdField.separator ? JSON.parse(f.properties[this.parcelleIdField.key]) : [f.properties[this.parcelleIdField.key]]))
        qParams.set('selected', ids.join(','))
        window.history.replaceState(null, null, window.location.pathname + '?' + qParams.toString())

        const select = [].concat(this.config.tooltip)
        if (this.linkField) select.push(this.linkField.key)
        if (this.attachmentField) select.push(this.attachmentField.key)
        if (this.config.colorField) select.push(this.config.colorField)
        const params = {
          size: 1000,
          select: select.join(','),
          qs: `${this.parcelleIdField.key}:(${ids.join(' OR ')})`
        }
        if (this.dateField) params.sort = this.dateField.key

        const { data } = await axios.get(`${this.datasetUrl}/lines`, { params })
        this.$store.commit('setAny', { features: data.results })
      }
    },
    initGeolocation () {
      this.geolocateControl = new maplibregl.GeolocateControl({
        showUserLocation: true,
        trackUserLocation: true,
        // positionOptions: {
        //   enableHighAccuracy: true
        // },
        fitBoundsOptions: {
          maxzoom: 15,
          animate: false
        }
      })
      this.map.addControl(this.geolocateControl, 'top-left')
      this.geolocateControl.on('error', err => {
        console.log('geolocate error', err)
      })
    },
    bboxPasted () {
      const bbox = JSON.stringify([].concat(...this.map.getBounds().toArray()).map(n => n.toFixed(6)))
      navigator.clipboard.writeText(bbox)
      if (window.parent) window.parent.postMessage({ type: 'set-config', content: { field: 'initialBounds', value: bbox } }, '*')
    },
    setPitch () {
      const zoom = this.map.getZoom()
      if (zoom < 12) return this.map.setPitch(0)
      if (zoom > 15) return this.map.setPitch(54)
      this.map.setPitch((zoom - 12) * 18)
    },
    params (p) {
      const params = new URLSearchParams(window.location.search)
      return params.get(p)
    },
    updateDataLayer () {
      this.map.addLayer({
        id: 'parcelle-dataset-infos',
        source: 'data-fair',
        'source-layer': 'results',
        type: 'fill',
        paint: {
          'fill-opacity': 0
        },
        minzoom: 13
      })

      const radius = this.config.radius || 10
      const pointsLayer = {
        id: 'parcelle-dataset-points',
        source: 'data-fair',
        'source-layer': 'results',
        type: 'circle',
        maxzoom: 13,
        minzoom: 2,
        paint: {
          'circle-radius': { stops: [[6, radius], [24, radius * 2]] },
          'circle-color': !this.config.colorField ? this.legend[0][1] : [].concat('match', [['to-string', ['get', this.config.colorField]]], ...this.legend.map(l => l.map(e => e.toString())), this.defaultColor),
          'circle-opacity': 0.7
        }
      }
      this.map.addLayer(pointsLayer, 'poi_label')

      if (this.config.tooltip.length) {
        this.map.on('mouseenter', 'parcelle-dataset-points', (e) => {
          this.map.getCanvas().style.cursor = 'pointer'
        })
        this.map.on('mouseleave', 'parcelle-dataset-points', () => {
          this.map.getCanvas().style.cursor = ''
        })
      }
    },
    toggleSatellite () {
      this.satellite = !this.satellite
      this.map.setLayoutProperty('satellite-tiles', 'visibility', this.satellite ? 'visible' : 'none')
    }
  }
}
</script>

<style lang="css">
</style>
